import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

export const paymentService = {
  // Get payments for current user
  async getMyPayments(userRole) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const column = userRole === 'photographer' ? 'photographer_id' : 'customer_id';
    
    // First, get payments
    const { data: payments, error } = await supabase
      .from('payments')
      .select('*')
      .eq(column, user?.id)
      .order('created_at', { ascending: false });

    if (error) {
      // If table doesn't exist, return empty array
      if (error.message?.includes('does not exist') || error.message?.includes('schema cache')) {
        console.warn('Payments table not found, returning empty array');
        return [];
      }
      throw error;
    }

    if (!payments || payments.length === 0) return [];

    // Get related bookings if needed
    const bookingIds = [...new Set(payments.map(p => p.booking_id).filter(Boolean))];
    let bookings = {};
    
    if (bookingIds.length > 0) {
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select('id, service_type, photography_type, booking_date, booking_time')
        .in('id', bookingIds);

      if (!bookingsError && bookingsData) {
        bookingsData.forEach(booking => {
          bookings[booking.id] = booking;
        });
      }
    }

    // Combine payments with bookings
    const enrichedPayments = payments.map(payment => ({
      ...payment,
      booking: bookings[payment.booking_id] || null
    }));

    return toCamelCase(enrichedPayments);
  },

  // Create payment
  async createPayment(paymentData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const paymentPayload = toSnakeCase({
      ...paymentData,
      customerId: user?.id,
      status: 'pending'
    });

    const { data, error } = await supabase?.from('payments')?.insert(paymentPayload)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  // Get earnings summary for photographer
  async getEarningsSummary() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('payments')
      .select('amount, net_amount, commission, status')
      .eq('photographer_id', user?.id);

    if (error) {
      // If table doesn't exist, return default values
      if (error.message?.includes('does not exist') || error.message?.includes('schema cache')) {
        console.warn('Payments table not found, returning default earnings summary');
        return { totalRevenue: 0, pendingPayments: 0, commission: 0 };
      }
      throw error;
    }

    if (!data || data.length === 0) {
      return { totalRevenue: 0, pendingPayments: 0, commission: 0 };
    }

    const summary = data?.reduce((acc, payment) => {
      if (payment?.status === 'completed') {
        acc.totalRevenue += parseFloat(payment?.net_amount || 0);
        acc.commission += parseFloat(payment?.commission || 0);
      } else if (payment?.status === 'pending') {
        acc.pendingPayments += parseFloat(payment?.amount || 0);
      }
      return acc;
    }, { totalRevenue: 0, pendingPayments: 0, commission: 0 });

    return summary;
  },

  /**
   * أرباح المصور من الحجوزات المدفوعة (مرتبطة فعلياً بالبيانات)
   * يستخدم جدول الحجوزات للحجوزات المؤكدة والمكتملة
   */
  async getPhotographerEarningsFromBookings() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: commissionRow } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'commission_settings')
      .maybeSingle();

    let commissionRate = 15;
    let minCommission = 50;
    if (commissionRow?.value) {
      try {
        const parsed = typeof commissionRow.value === 'string' ? JSON.parse(commissionRow.value) : commissionRow.value;
        commissionRate = parsed?.platformCommissionRate ?? 15;
        minCommission = parsed?.minCommission ?? 50;
      } catch (_) {}
    }

    const { data: paidBookings, error } = await supabase
      .from('bookings')
      .select('id, final_price_sar, price, pricing_snapshot, status, booking_date, created_at, photography_type, city, customer_id, photographer_payout_status, photographer_paid_at')
      .eq('assigned_photographer_id', user.id)
      .in('status', ['paid_confirmed', 'completed', 'in_progress'])
      .order('created_at', { ascending: false });

    if (error) throw error;

    const transactions = (paidBookings || []).map((b) => {
      const amount = parseFloat(b.final_price_sar || b.price || b.pricing_snapshot?.total || 0);
      const commission = Math.max(Math.round(amount * (commissionRate / 100)), minCommission);
      const netAmount = amount - commission;
      return {
        id: b.id,
        bookingId: b.id,
        amount,
        commission,
        netAmount,
        status: b.status === 'completed' ? 'completed' : 'pending',
        payoutStatus: b.photographer_payout_status === 'paid' ? 'paid' : b.photographer_payout_status === 'refunded' ? 'refunded' : 'pending',
        paidAt: b.photographer_paid_at,
        date: b.created_at,
        bookingDate: b.booking_date,
        photographyType: b.photography_type,
        city: b.city,
      };
    });

    const totalEarnings = transactions.reduce((s, t) => s + t.netAmount, 0);
    const totalCommission = transactions.reduce((s, t) => s + t.commission, 0);
    const totalRevenue = transactions.reduce((s, t) => s + t.amount, 0);

    return {
      summary: { totalEarnings, totalCommission, totalRevenue },
      transactions: toCamelCase(transactions),
      commissionRate,
    };
  },
};